import * as React from "react";
import { Check, Circle, Sparkles } from "lucide-react";
import { Button, Progress } from "@/components/ui";
import { HoloShimmer } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { DesignPackageProgress } from "@/utils/actioncable";
import { useInterval } from "@/utils/useInterval";
import { LOADING_TIPS } from "../loadingTips";
import { percentFor, Stage, STAGES, stageForStatus } from "../progress";

export type ProgressSnapshot = DesignPackageProgress;

interface GenerationProgressProps {
  pkg: DesignPackageDetail;
  progress: ProgressSnapshot | undefined;
  canEdit: boolean;
  onBackToList(): void;
}

const TIP_INTERVAL_MS = 8_000;
const SLOW_AFTER_MS = 120_000;
const SLOW_CHECK_MS = 1_000;

const StageStepper: React.FC<{ current: Stage }> = ({ current }) => (
  <ol className="flex flex-wrap justify-center gap-x-6 gap-y-2" data-testid="stage-stepper">
    {STAGES.map(({ stage, label }) => {
      const state = stage < current ? "done" : stage === current ? "current" : "pending";
      return (
        <li
          key={stage}
          data-state={state}
          aria-current={state === "current" ? "step" : undefined}
          className={cn(
            "flex items-center gap-1.5 text-sm",
            state === "pending" ? "text-slate-400" : "text-slate-700",
            state === "current" && "font-semibold"
          )}
        >
          {state === "done" && <Check className="size-4 text-emerald-500" aria-hidden="true" />}
          {state === "current" && (
            <Sparkles className="size-4 text-primary animate-pulse motion-reduce:animate-none" aria-hidden="true" />
          )}
          {state === "pending" && <Circle className="size-4" aria-hidden="true" />}
          {label}
        </li>
      );
    })}
  </ol>
);

export const GenerationProgress: React.FC<GenerationProgressProps> = ({ pkg, progress, onBackToList }) => {
  const stage = stageForStatus(pkg.status);
  const current: Stage = typeof stage === "number" ? stage : 4;
  const packageProgress = progress?.designPackageItemId == null ? progress : undefined;
  const sublabel = packageProgress?.progressStatus ?? pkg.progressStatus ?? undefined;
  const percent = percentFor(pkg.status, packageProgress?.percent ?? pkg.percent);

  const tips = React.useMemo(
    () => LOADING_TIPS.filter(tip => tip.appliesTo.includes(pkg.contextType)),
    [pkg.contextType]
  );
  const [tipIndex, setTipIndex] = React.useState(0);
  useInterval(
    () => setTipIndex(i => (i + 1) % Math.max(tips.length, 1)),
    tips.length > 1 ? TIP_INTERVAL_MS : undefined
  );
  const tip = tips[tipIndex % Math.max(tips.length, 1)];

  const lastChangeAt = React.useRef(Date.now());
  const [slow, setSlow] = React.useState(false);
  const progressKey = `${pkg.status}|${percent}|${sublabel ?? ""}|${progress?.at ?? ""}`;
  React.useEffect(() => {
    lastChangeAt.current = Date.now();
    setSlow(false);
  }, [progressKey]);
  useInterval(() => {
    if (!slow && Date.now() - lastChangeAt.current >= SLOW_AFTER_MS) setSlow(true);
  }, SLOW_CHECK_MS);

  return (
    <div className="flex flex-col items-center gap-6 text-center" data-testid="generation-progress">
      <div className="w-full max-w-[360px]">
        <HoloShimmer aspectRatio={pkg.aspectRatio} label={STAGES[current - 1].label} sublabel={sublabel} />
      </div>

      <StageStepper current={current} />

      <div className="w-full max-w-md flex items-center gap-3">
        <Progress value={percent} className="flex-1" aria-label="Progress" />
        <span className="text-sm tabular-nums text-slate-500" data-testid="progress-percent">
          {percent}%
        </span>
      </div>

      <p className="text-sm text-slate-600 max-w-md">
        Usually takes about a minute. You can leave this page. We'll keep working and it'll be waiting in your list.
      </p>

      <Button variant="outline" onClick={onBackToList}>
        Back to designs
      </Button>

      <div className="flex flex-col gap-1 text-sm text-slate-500 max-w-md" aria-live="polite">
        {slow && <p className="font-medium text-slate-700">Taking longer than usual. We're still working.</p>}
        {tip && <p data-testid="loading-tip">While you wait: {tip.text}</p>}
      </div>
    </div>
  );
};
